/**
 * Хук для стандартизации мутаций с автоматической инвалидацией кеша
 * 
 * Оборачивает useMutation и добавляет:
 * - Опциональную инвалидацию кеша
 * - Единообразную обработку ошибок
 */

import { useMutation, useQueryClient, type UseMutationOptions, type MutationFunction } from '@tanstack/react-query';
import { handleApiError } from '@/lib/errors';

interface UseMutationWithCacheOptions<TData, TError, TVariables> 
  extends Omit<UseMutationOptions<TData, TError, TVariables>, 'onSuccess' | 'onError'> {
  /** Функция для инвалидации кеша после успешной мутации */
  invalidateCache?: (queryClient: ReturnType<typeof useQueryClient>, data: TData, variables: TVariables) => void;
  /** Кастомная обработка успеха */
  onSuccessCallback?: (data: TData, variables: TVariables) => void;
  /** Кастомная обработка ошибки */
  onErrorCallback?: (error: TError, variables: TVariables) => void;
}

/**
 * Хук для создания мутации с инвалидацией кеша
 */
export function useMutationWithCache<TData = unknown, TError extends Error = Error, TVariables = void>({
  mutationFn,
  invalidateCache,
  onSuccessCallback,
  onErrorCallback,
  ...mutationOptions
}: UseMutationWithCacheOptions<TData, TError, TVariables> & {
  mutationFn: MutationFunction<TData, TVariables>;
}): ReturnType<typeof useMutation<TData, TError, TVariables>> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    ...mutationOptions,
    onSuccess: (data, variables) => {
      // Инвалидируем кеш если указано
      if (invalidateCache) {
        invalidateCache(queryClient, data, variables);
      }

      // Вызываем кастомный коллбек если указан
      if (onSuccessCallback) {
        onSuccessCallback(data, variables);
      }
    },
    onError: (error, variables) => {
      // Логируем ошибку через централизованную систему обработки ошибок
      handleApiError(error, { consolePrefix: 'Mutation error' });

      // Вызываем кастомный коллбек если указан
      if (onErrorCallback) {
        onErrorCallback(error, variables);
      }
    },
  });
}